/**
 * The Ghost — design tokens.
 *
 * A single source of truth for the site's look. Import this object anywhere
 * (styled-components template literals, inline styles, plain TS) instead of
 * hard-coding hex values.
 *
 * Every token is also emitted as a CSS custom property by `styles.ts`, so raw
 * CSS can reach them as `var(--color-blood)` etc.
 */

export const color = {
  /* Surfaces — near-black, cold, slightly blue-shifted */
  void: "#050506",
  abyss: "#08080b",
  crypt: "#0e0e12",
  slab: "#141419",
  slabRaised: "#1b1b21",

  /* Lines & dividers */
  ash: "#26262e",
  ashLight: "#33333d",

  /* Text */
  smoke: "#7b7b87",
  bone: "#b6b1a8",
  pale: "#eceae5",
  white: "#ffffff",

  /* Accents — dried blood through to live ember */
  blood: "#8e1616",
  bloodBright: "#b81d1d",
  ember: "#e2593c",

  /* Translucent accent washes */
  bloodSoft: "rgba(184, 29, 29, 0.14)",
  bloodFaint: "rgba(184, 29, 29, 0.06)",
  emberSoft: "rgba(226, 89, 60, 0.18)",

  /* Overlays */
  scrim: "rgba(5, 5, 6, 0.88)",
  scrimSoft: "rgba(5, 5, 6, 0.55)",

  /* Ink on paper — the feature cards are a light surface, so they need their
     own text ramp. Measured against the darkest 5% of ui/paper.webp, which is
     where legibility actually fails: heading 5.1:1, body 4.5:1, icon 3.6:1.
     Note that DARKENING the paper would make these worse, not better — dark
     ink on light stock, so the two move together. */
  paper: "#bcaa96",
  ink: "#231a12",
  inkBody: "#2e241b",
  inkAccent: "#6d1010",
} as const;

export const font = {
  /** Condensed, uppercase, wide-tracked — headlines and the wordmark. */
  display: `"Oswald", "Motiva Sans Bold", "Haettenschweiler", "Arial Narrow Bold", sans-serif`,
  /** Typewriter — eyebrows, labels, metadata. The "case file" voice. */
  label: `"Special Elite", "Courier New", Courier, monospace`,
  /** Body copy. Motiva Sans ships with the project already. */
  body: `"Motiva Sans Light", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
} as const;

/** Fluid type scale. clamp(min, preferred, max) — no media queries needed. */
export const size = {
  hero: "clamp(3.25rem, 12vw, 9rem)",
  h1: "clamp(2.4rem, 6.5vw, 4.25rem)",
  h2: "clamp(1.9rem, 4.4vw, 3rem)",
  h3: "clamp(1.35rem, 2.6vw, 1.9rem)",
  h4: "clamp(1.1rem, 1.9vw, 1.3rem)",
  lead: "clamp(1.05rem, 1.7vw, 1.3rem)",
  body: "1.0625rem",
  small: "0.9375rem",
  label: "0.8125rem",
  micro: "0.6875rem",
} as const;

export const space = {
  xxs: "0.25rem",
  xs: "0.5rem",
  sm: "0.75rem",
  md: "1rem",
  lg: "1.5rem",
  xl: "2rem",
  xxl: "3rem",
  section: "clamp(3.5rem, 7vw, 6.5rem)",
} as const;

export const radius = {
  sm: "2px",
  md: "4px",
  lg: "8px",
  pill: "999px",
} as const;

export const shadow = {
  panel: "0 24px 60px rgba(0, 0, 0, 0.65)",
  lift: "0 18px 40px rgba(0, 0, 0, 0.55)",
  bloodGlow: "0 0 0 1px rgba(184, 29, 29, 0.35), 0 0 34px rgba(184, 29, 29, 0.22)",
  emberGlow: "0 0 28px rgba(226, 89, 60, 0.28)",
  textGlow: "0 0 18px rgba(226, 89, 60, 0.35)",
} as const;

export const motion = {
  fast: "180ms",
  base: "320ms",
  slow: "620ms",
  drift: "9s",
  ease: "cubic-bezier(0.16, 1, 0.3, 1)",
  easeOut: "cubic-bezier(0.22, 0.61, 0.36, 1)",
} as const;

export const layer = {
  atmosphere: 1,
  content: 2,
  header: 50,
  drawer: 60,
  modal: 70,
  toast: 80,
} as const;

/** Breakpoints in px. Use via the `media` helpers below. */
export const bp = {
  xs: 414,
  sm: 576,
  md: 768,
  lg: 1024,
  xl: 1200,
} as const;

export const media = {
  xs: `@media only screen and (max-width: ${bp.xs}px)`,
  sm: `@media only screen and (max-width: ${bp.sm}px)`,
  md: `@media only screen and (max-width: ${bp.md}px)`,
  lg: `@media only screen and (max-width: ${bp.lg}px)`,
  xl: `@media only screen and (max-width: ${bp.xl}px)`,
  /** Wrap anything animated in this so motion-sensitive users opt out. */
  motionSafe: `@media (prefers-reduced-motion: no-preference)`,
  motionReduce: `@media (prefers-reduced-motion: reduce)`,
  hover: `@media (hover: hover) and (pointer: fine)`,
} as const;

export const maxWidth = {
  page: "1200px",
  prose: "68ch",
  narrow: "760px",
} as const;

const theme = {
  color,
  font,
  size,
  space,
  radius,
  shadow,
  motion,
  layer,
  bp,
  media,
  maxWidth,
};

export type Theme = typeof theme;
export default theme;
